import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { Chip } from "@mui/material";
import Button from "@mui/material/Button";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { ApiErrorDisplayAccordion } from "core/components/ApiErrorDisplayAccordion";
import { DialogFormContainer } from "core/components/DialogFormContainer";
import { DropZone } from "core/components/ReactHookFormComponents/DropZone";
import { ReactHookCheckbox } from "core/components/ReactHookFormComponents/ReactHookCheckbox";
import { ReactHookFormSelect } from "core/components/ReactHookFormComponents/ReactHookFormSelect";
import { ReactHookTextField } from "core/components/ReactHookFormComponents/ReactHookTextField";
import { SvgFileIcon } from "core/components/SvgFileIcon";
import type { RemoteServiceErrorResponse } from "core/entities/CentrixTypes";
import { useCentrixFetch } from "core/hooks/useCentrixFetch";
import { useToaster } from "core/hooks/useToaster";
import { uploadToAzureBlobStorage } from "core/utils/AzureBlobStorage";
import { getAzureUploadProgressEventHandler } from "core/utils/AzureBlobStorage/azureUpload";
import { exhaustiveCheck } from "core/utils/exhaustiveCheck";
import { normalizeExtension } from "core/utils/normalizeExtension";
import { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { REQUEST_TYPE_OPTIONS, useRequestTypeFormatter } from "../shared";

const ACCEPTED_FILE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/csv",
  "text/plain",
  "text/markdown",
];

const handleUpload = async (
  centrixFetch: ReturnType<typeof useCentrixFetch>,
  file: File,
  uploadProgressCallback?: (progress: number) => void
): Promise<
  | { ok: true; fileId: string; fileName: string }
  | { ok: false; error: "TokenError" | "UploadError" | "ConfirmationError" }
> => {
  const uploadTokenResponse = await centrixFetch({
    method: "post",
    path: "/api/app/suggestion/create-upload-token",
    body: {
      fileName: normalizeExtension(file.name),
    },
  });
  if (!uploadTokenResponse.ok) {
    uploadProgressCallback?.(0);
    return { ok: false, error: "TokenError" };
  }
  uploadProgressCallback?.(30);
  const { accessToken, fileUploadValidationId } =
    await uploadTokenResponse.json();

  const uploadResponse = await uploadToAzureBlobStorage(
    file,
    accessToken,
    getAzureUploadProgressEventHandler(uploadProgressCallback, 30, 60)
  );
  if (!uploadResponse.ok) {
    uploadProgressCallback?.(0);
    return { ok: false, error: "UploadError" };
  }
  uploadProgressCallback?.(60);
  const confirmationRes = await centrixFetch({
    method: "put",
    path: "/api/app/suggestion/confirm-upload",
    body: {
      fileUploadValidationId,
      uploadAccessToken: accessToken,
    },
  });
  if (!confirmationRes.ok) {
    uploadProgressCallback?.(0);
    return { ok: false, error: "ConfirmationError" };
  }
  uploadProgressCallback?.(100);
  return {
    ok: true,
    fileId: fileUploadValidationId,
    fileName: normalizeExtension(file.name),
  };
};

type SuggestionForm = {
  title: string;
  requestType: string;
  area: string;
  content: string;
  importanceReason: string;
  canContact: boolean;
  attachments?: { fileId: string; fileName: string }[];
};

const COUNTDOWN_SECONDS = 5;
const MAX_TITLE_LENGTH = 256;
const MAX_CONTENT_LENGTH = 2000;
const MAX_IMPORTANCE_REASON_LENGTH = 2000;
const MAX_AREA_LENGTH = 256;

export function SuggestionBoxDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation("Fixhub", {
    keyPrefix: "Next:Core:SuggestionBoxDialog",
  });
  const centrixFetch = useCentrixFetch();
  const { errorToast } = useToaster();
  const [errorResponse, setErrorResponse] =
    useState<RemoteServiceErrorResponse | null>(null);
  const [fileUploadProgress, setFileUploadProgress] = useState<number | null>(
    null
  );
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);
  const countdownRef = useRef<ReturnType<typeof setInterval>>(undefined);
  const requestTypeFormatter = useRequestTypeFormatter();

  const {
    handleSubmit,
    reset,
    control,
    formState: { isSubmitting, isSubmitSuccessful },
    watch,
    getValues,
    setValue,
  } = useForm<SuggestionForm>({
    defaultValues: {
      title: "",
      requestType: "",
      area: "",
      content: "",
      importanceReason: "",
      canContact: false,
      attachments: [],
    },
  });
  const attachments = watch("attachments") || [];
  const uploadTokenError = t("uploadTokenError", "Failed to get upload token");
  const fileInputUploadErrorMessage = t(
    "fileInputUploadError",
    "Failed to upload file"
  );
  const confirmationErrorMessage = t(
    "confirmationError",
    "Failed to confirm file upload"
  );

  const onFileReceived = useCallback(
    async (file: File) => {
      setFileUploadProgress(1);
      const uploadResult = await handleUpload(
        centrixFetch,
        file,
        setFileUploadProgress
      );
      if (!uploadResult.ok) {
        switch (uploadResult.error) {
          case "TokenError":
            errorToast(uploadTokenError);
            break;
          case "UploadError":
            errorToast(fileInputUploadErrorMessage);
            break;
          case "ConfirmationError":
            errorToast(confirmationErrorMessage);
            break;
          default:
            exhaustiveCheck(uploadResult.error);
        }
        setFileUploadProgress(null);
        return { fileNameValue: "", nameValue: "" };
      }
      // handle success
      setTimeout(() => {
        setFileUploadProgress(null);
      }, 500);
      return {
        nameValue: uploadResult.fileId,
        fileNameValue: uploadResult.fileName,
      };
    },
    [
      centrixFetch,
      errorToast,
      uploadTokenError,
      fileInputUploadErrorMessage,
      confirmationErrorMessage,
    ]
  );

  const handleClose = useCallback(() => {
    clearInterval(countdownRef.current);
    setCountdown(COUNTDOWN_SECONDS);
    setErrorResponse(null);
    reset();
    onClose();
  }, [onClose, reset]);

  useEffect(() => {
    if (!isSubmitSuccessful) {
      return;
    }
    setCountdown(COUNTDOWN_SECONDS);
    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          handleClose();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(countdownRef.current);
  }, [isSubmitSuccessful, handleClose]);
  const errorMessage = t("submitError", "Failed to submit suggestion");

  const onSubmit = async (data: SuggestionForm) => {
    setErrorResponse(null);
    const response = await centrixFetch({
      method: "post",
      path: "/api/app/suggestion",
      body: {
        title: data.title,
        content: data.content,
        requestType: data.requestType,
        importanceReason: data.importanceReason,
        area: data.area || undefined,
        canContact: data.canContact,
        attachments: data.attachments?.map((att) => ({ fileId: att.fileId })),
      },
    });
    if (!response.ok) {
      const error = await response.json();
      setErrorResponse(error as RemoteServiceErrorResponse);
      errorToast(errorMessage);
      throw new Error("Submission failed");
    }
  };
  const handleAttachmentDelete = (fileId: string) => () => {
    const newAttachments =
      getValues("attachments")?.filter((att) => att.fileId !== fileId) || [];
    setValue("attachments", newAttachments);
  };

  const maxAttachmentsError = t(
    "maxAttachmentsError",
    "You can only upload up to 5 attachments"
  );

  return (
    <DialogFormContainer
      title={t("dialogTitle", "Suggestion Box")}
      open={open}
      onDialogClose={(_, reason) => {
        if (reason !== "backdropClick" || !isSubmitting) {
          handleClose();
        }
      }}
      onSubmit={handleSubmit(onSubmit)}
      onCancelButtonClick={handleClose}
      isSubmitting={isSubmitting}
      hideActions={isSubmitSuccessful}
      maxWidth="sm"
    >
      {isSubmitSuccessful ? (
        <Stack alignItems="center" spacing={2} py={3}>
          <CheckCircleOutlineIcon color="success" sx={{ fontSize: 64 }} />
          <Typography variant="h6">
            {t("successMessage", "Thank you for your suggestion!")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("closingIn", "This dialog will close in {{seconds}}s...", {
              seconds: countdown,
            })}
          </Typography>
          <Button variant="outlined" onClick={handleClose}>
            {t("closeButton", "Close")}
          </Button>
        </Stack>
      ) : (
        <Stack spacing={2} mt={1}>
          <ReactHookTextField
            name="title"
            control={control}
            label={t("titleLabel", "Title")}
            required
            fullWidth
            maxLength={MAX_TITLE_LENGTH}
            rules={{
              maxLength: {
                value: MAX_TITLE_LENGTH,
                message: t("titleMaxLength", "Maximum {{max}} characters", {
                  max: MAX_TITLE_LENGTH,
                }),
              },
            }}
          />
          <ReactHookFormSelect
            name="requestType"
            control={control}
            label={t("requestTypeLabel", "Request Type")}
            options={REQUEST_TYPE_OPTIONS.map((option) => ({
              value: option.key,
              label: requestTypeFormatter(option.key),
            }))}
            rules={{
              required: t("requestTypeRequired", "This field is required"),
            }}
          />
          <ReactHookTextField
            name="area"
            control={control}
            label={t("areaLabel", "Area (optional)")}
            fullWidth
            maxLength={MAX_AREA_LENGTH}
            rules={{
              maxLength: {
                value: MAX_AREA_LENGTH,
                message: t("areaMaxLength", "Maximum {{max}} characters", {
                  max: MAX_AREA_LENGTH,
                }),
              },
            }}
          />
          <ReactHookTextField
            name="content"
            control={control}
            label={t("contentLabel", "Your suggestion")}
            required
            fullWidth
            multiline
            minRows={4}
            maxRows={10}
            maxLength={MAX_CONTENT_LENGTH}
            textFieldProps={{
              placeholder: t("contentPlaceholder", "Tell us your idea..."),
            }}
            rules={{
              maxLength: {
                value: MAX_CONTENT_LENGTH,
                message: t("contentMaxLength", "Maximum {{max}} characters", {
                  max: MAX_CONTENT_LENGTH,
                }),
              },
            }}
          />
          <ReactHookTextField
            name="importanceReason"
            control={control}
            label={t("importanceReasonLabel", "Why is this important?")}
            required
            fullWidth
            multiline
            minRows={2}
            maxRows={6}
            maxLength={MAX_IMPORTANCE_REASON_LENGTH}
            rules={{
              maxLength: {
                value: MAX_IMPORTANCE_REASON_LENGTH,
                message: t(
                  "importanceReasonMaxLength",
                  "Maximum {{max}} characters",
                  { max: MAX_IMPORTANCE_REASON_LENGTH }
                ),
              },
            }}
          />
          <ReactHookCheckbox
            name="canContact"
            control={control}
            label={t(
              "canContactLabel",
              "I can be contacted about this suggestion"
            )}
            labelPlacement="end"
          />
          <Typography variant="subtitle2" gutterBottom>
            {t("attachmentsLabel", {
              defaultValue: "Attachments (maximum {{max}})",
              max: 5,
            })}
          </Typography>
          <DropZone
            inputProps={{ accept: ACCEPTED_FILE_TYPES.join(",") }}
            disabled={attachments.length >= 5 || isSubmitting}
            handleFile={async (file) => {
              const { fileNameValue, nameValue } = await onFileReceived(file);
              if (!fileNameValue || !nameValue) {
                return;
              }
              const currentAttachments = getValues("attachments") || [];
              if (currentAttachments.length >= 5) {
                errorToast(maxAttachmentsError);
                return;
              }
              setValue("attachments", [
                ...(currentAttachments || []),
                { fileId: nameValue, fileName: fileNameValue },
              ]);
            }}
            name="attachments"
          />
          {fileUploadProgress !== null && (
            <LinearProgress
              variant="determinate"
              value={fileUploadProgress ?? 0}
            />
          )}
          <Stack
            direction="row"
            gap={1 / 2}
            alignItems="center"
            flexWrap="wrap"
          >
            {attachments.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                {t("noAttachments", "No attachments uploaded")}
              </Typography>
            )}
            {attachments.map((att) => {
              if (!att.fileId || !att.fileName) {
                return null;
              }
              return (
                <Chip
                  key={att.fileId}
                  icon={<SvgFileIcon fileName={att.fileName} />}
                  onDelete={handleAttachmentDelete(att.fileId)}
                  label={att.fileName}
                />
              );
            })}
          </Stack>
          <ApiErrorDisplayAccordion errorResponse={errorResponse} />
        </Stack>
      )}
    </DialogFormContainer>
  );
}
